import React from 'react';
import {
  MagnifyingGlassIcon,
  FunnelIcon,
} from '@heroicons/react/24/outline';

// Completely static header component
const StaticMenuHeader: React.FC = React.memo(() => (
  <div className="bg-white shadow-sm border-b border-gray-200">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="text-center mb-8">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">
          🍽️ Our Menu
        </h1>
        <p className="text-xl text-gray-600">
          Delicious food, voice-enabled ordering
        </p>
      </div>
    </div>
  </div>
));

// Category Filter component - completely isolated
const CategoryFilter: React.FC<{
  selectedCategory: string;
  categories: any[];
  onCategoryChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
}> = ({ selectedCategory, categories, onCategoryChange }) => {
  return (
    <div className="flex items-center space-x-2">
      <FunnelIcon className="w-5 h-5 text-gray-400" />
      <select
        value={selectedCategory}
        onChange={onCategoryChange}
        className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
      >
        <option value="all">All Categories</option>
        {categories.map((category) => (
          <option key={category.name} value={category.name}>
            {category.name}
          </option>
        ))}
      </select>
    </div>
  );
};

// Search Input component - completely isolated
const SearchInput: React.FC<{
  searchTerm: string;
  onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}> = ({ searchTerm, onSearchChange }) => {
  const [localSearchTerm, setLocalSearchTerm] = React.useState(searchTerm);
  const timeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  // Update local state when parent searchTerm changes (for external updates like clear filters)
  React.useEffect(() => {
    setLocalSearchTerm(searchTerm);
  }, [searchTerm]);

  // Handle input change with debouncing
  const handleInputChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setLocalSearchTerm(newValue);

    // Clear previous timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Create a synthetic event for the debounced callback
    const syntheticEvent = {
      target: { value: newValue }
    } as React.ChangeEvent<HTMLInputElement>;

    // Debounce the parent callback
    timeoutRef.current = setTimeout(() => {
      onSearchChange(syntheticEvent);
    }, 150);
  }, [onSearchChange]);

  // Cleanup timeout on unmount
  React.useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <div className="relative flex-1 max-w-md">
      <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
      <input
        type="text"
        placeholder="Search menu items..."
        value={localSearchTerm}
        onChange={handleInputChange}
        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
      />
    </div>
  );
};

// Search and Filter component - using absolute isolation
const SearchAndFilters: React.FC<{
  searchTerm: string;
  selectedCategory: string;
  categories: any[];
  onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onCategoryChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
}> = ({
  searchTerm,
  selectedCategory,
  categories,
  onSearchChange,
  onCategoryChange,
}) => {
  return (
    <div className="bg-white border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
          {/* Search Input - Isolated */}
          <div key="search-section">
            <SearchInput
              searchTerm={searchTerm}
              onSearchChange={onSearchChange}
            />
          </div>

          {/* Category Filter - Isolated */}
          <div key="category-section">
            <CategoryFilter
              selectedCategory={selectedCategory}
              categories={categories}
              onCategoryChange={onCategoryChange}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

// Static layout wrapper
interface StaticMenuLayoutProps {
  searchTerm: string;
  selectedCategory: string;
  categories: any[];
  onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onCategoryChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  children: React.ReactNode;
}

const StaticMenuLayout: React.FC<StaticMenuLayoutProps> = ({
  searchTerm,
  selectedCategory,
  categories,
  onSearchChange,
  onCategoryChange,
  children
}) => {
  return (
    <div className="min-h-screen bg-gray-50">
      <StaticMenuHeader />

      {/* Search and Filters - Memoized to prevent re-render */}
      <SearchAndFilters
        searchTerm={searchTerm}
        selectedCategory={selectedCategory}
        categories={categories}
        onSearchChange={onSearchChange}
        onCategoryChange={onCategoryChange}
      />

      {/* Dynamic Content Area */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {children}
      </div>
    </div>
  );
};

export default StaticMenuLayout;